/**
 * desktop-presence — pure helpers for the menu-bar tray line and the
 * client-side reminders. No Angular, no window, so they unit-test bare.
 *
 * USED BY: desktop-tray.service.ts, desktop-reminder.service.ts.
 */
import type { ProfileBooking } from './services/profile-data.service';
import { formatPrefs } from './utils/format-prefs';
import { formatWallClock } from './utils/locale-date';

/** How far ahead of a meeting the reminder fires. */
export const MEETING_REMINDER_MS = 5 * 60_000;

/** Menu-bar text budget — macOS truncates long titles with an ellipsis anyway. */
const TITLE_MAX = 26;

export interface TrayPresence {
  /** Text beside the menu-bar icon, or null to show the icon alone. */
  title: string | null;
  /** The informational first row of the tray menu. */
  nextLabel: string;
  /** Whether "Check in" is actionable right now. */
  checkInEnabled: boolean;
  /** The desk booking "Check in" acts on, when enabled. */
  checkInBookingId: string | null;
}

function spaceName(b: ProfileBooking): string {
  return b.spaces[0]?.name ?? b.subject ?? 'Meeting';
}

function clock(iso: string): string {
  const d = new Date(iso);
  // House style is a 12-hour clock; a viewer who chose 24-hour gets "14:05".
  if (formatPrefs().timeFormat === 'h23') return formatWallClock(d.getHours(), d.getMinutes());
  const h = d.getHours() % 12 || 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}${d.getHours() < 12 ? 'am' : 'pm'}`;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}

/** Whole minutes until `iso`, floored at 0. */
export function minutesUntil(iso: string, now: number): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - now) / 60_000));
}

/** A desk booking whose window is open and which is not checked in yet. */
export function deskCheckInAvailable(desk: ProfileBooking | null, now: number): boolean {
  if (!desk || desk.checkedIn || desk.cancelled || desk.released) return false;
  const start = new Date(desk.startTime).getTime();
  const end = new Date(desk.endTime).getTime();
  return start <= now && now < end;
}

/**
 * What the tray should show. `meetings` are today's meeting-room bookings in
 * start order (ProfileDataService.meetingBookings); `desk` today's desk.
 */
export function trayPresence(
  meetings: readonly ProfileBooking[],
  desk: ProfileBooking | null,
  now: number,
): TrayPresence {
  const live = meetings.filter((m) => !m.cancelled && new Date(m.endTime).getTime() > now);
  const current = live.find((m) => new Date(m.startTime).getTime() <= now) ?? null;
  const upcoming = live.find((m) => new Date(m.startTime).getTime() > now) ?? null;

  let title: string | null = null;
  let nextLabel = 'Nothing else today';
  if (current) {
    const name = spaceName(current);
    title = truncate(`${name} · until ${clock(current.endTime)}`, TITLE_MAX);
    nextLabel = `Now: ${name} · until ${clock(current.endTime)}`;
  } else if (upcoming) {
    const name = spaceName(upcoming);
    const mins = minutesUntil(upcoming.startTime, now);
    const when = mins <= 90 ? `in ${mins} min` : `at ${clock(upcoming.startTime)}`;
    title = truncate(`${name} · ${when}`, TITLE_MAX);
    nextLabel = `Next: ${name} · ${when}`;
  }

  const checkInEnabled = deskCheckInAvailable(desk, now);
  return {
    title,
    nextLabel,
    checkInEnabled,
    checkInBookingId: checkInEnabled && desk ? desk._id : null,
  };
}

export type ReminderKind = 'meeting-start' | 'desk-check-in';

export interface DueReminder {
  /** Stable per booking + kind, so a reminder fires once per booking. */
  key: string;
  kind: ReminderKind;
  title: string;
  body: string;
}

/**
 * Reminders whose moment is NOW: a meeting starting within the next five
 * minutes, and a desk booking whose check-in window has opened without a
 * check-in. `fired` holds keys already delivered (persisted across relaunch).
 */
export function dueReminders(
  meetings: readonly ProfileBooking[],
  desk: ProfileBooking | null,
  now: number,
  fired: ReadonlySet<string>,
): DueReminder[] {
  const due: DueReminder[] = [];
  for (const m of meetings) {
    if (m.cancelled) continue;
    const start = new Date(m.startTime).getTime();
    const lead = start - now;
    if (lead <= 0 || lead > MEETING_REMINDER_MS) continue;
    const key = `${m._id}:meeting-start`;
    if (fired.has(key)) continue;
    const mins = Math.max(1, Math.round(lead / 60_000));
    due.push({
      key,
      kind: 'meeting-start',
      title: `${m.subject?.trim() || 'Meeting'} starts in ${mins} min`,
      body: `${spaceName(m)} · ${clock(m.startTime)}–${clock(m.endTime)}`,
    });
  }
  if (desk && deskCheckInAvailable(desk, now)) {
    const key = `${desk._id}:desk-check-in`;
    if (!fired.has(key)) {
      due.push({
        key,
        kind: 'desk-check-in',
        title: 'Check in to your desk',
        body: `${spaceName(desk)} is yours until ${clock(desk.endTime)} — check in so it stays that way.`,
      });
    }
  }
  return due;
}
